import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Container, Form, Button, Alert, Row, Col, ProgressBar, Card } from 'react-bootstrap';

const SetupPassphrase: React.FC = () => {
  const { completeSetup, userEmail } = useAuth();
  const navigate = useNavigate();
  const [passphrase, setPassphrase] = useState('');
  const [confirmPassphrase, setConfirmPassphrase] = useState('');
  const [rememberPassphrase, setRememberPassphrase] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Password strength calculation
  const calculateStrength = (pass: string): number => {
    if (!pass) return 0;
    let strength = 0;
    
    // Length check
    if (pass.length >= 8) strength += 25;
    
    // Complexity checks
    if (/[A-Z]/.test(pass)) strength += 15; // Has uppercase
    if (/[a-z]/.test(pass)) strength += 15; // Has lowercase
    if (/[0-9]/.test(pass)) strength += 15; // Has number
    if (/[^A-Za-z0-9]/.test(pass)) strength += 15; // Has special char
    if (pass.length >= 12) strength += 15; // Extra length bonus
    
    return Math.min(100, strength);
  };

  const strength = calculateStrength(passphrase);
  
  const getStrengthColor = (): string => {
    if (strength < 30) return 'danger';
    if (strength < 50) return 'warning';
    if (strength < 80) return 'info';
    return 'success';
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Reset states
    setError(null);
    setSuccess(false);
    
    // Validation
    if (passphrase.length < 8) {
      setError('Passphrase must be at least 8 characters');
      return;
    }
    
    if (passphrase !== confirmPassphrase) {
      setError('Passphrases do not match');
      return;
    }
    
    if (strength < 50) {
      setError('Please use a stronger passphrase with a mix of letters, numbers, and special characters');
      return;
    }
    
    setLoading(true);
    
    try {
      // Call the setup function from auth context, passing the rememberPassphrase option
      await completeSetup(passphrase, rememberPassphrase);

      // Show success message
      setSuccess(true);
      
      // Redirect to home after a short delay
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (err: any) {
      // Extract more useful error message if available
      let errorMessage = 'Failed to set up your account. Please try again.';
      
      if (err.message && err.message.includes('API error:')) {
        errorMessage = `Server error: ${err.message.split('API error:')[1].trim()}`;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Row className="justify-content-center">
        <Col md={12} lg={10} xl={8}>
          <Card className="shadow">
            <Card.Header as="h3" className="text-center bg-primary text-white">
              Complete Your Account Setup
            </Card.Header>
            <Card.Body className="p-4">
              {success ? (
                <Alert variant="success">
                  <Alert.Heading>Setup Complete!</Alert.Heading>
                  <p>Your account has been successfully set up with end-to-end encryption.</p>
                  <p>Redirecting you to the home page...</p>
                </Alert>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Alert variant="info">
                    <Alert.Heading>Welcome, {userEmail}!</Alert.Heading>
                    <p>
                      You need to create a secure passphrase to encrypt your data. This passphrase will be 
                      used to protect your encryption keys and cannot be recovered if lost.
                    </p>
                    <p>
                      Each device you use will require this passphrase to access your encrypted data.
                    </p>
                  </Alert>
                  <Alert variant="warning">Make sure you save this passphrase in your password manager, or 
                  someplace that you won't lose it. If you lose this passphrase, data that belongs only
                  to you will be lost.</Alert>

                  {error && <Alert variant="danger">{error}</Alert>}
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Create a Passphrase</Form.Label>
                    <Form.Control
                      type="password"
                      value={passphrase}
                      onChange={(e) => setPassphrase(e.target.value)}
                      placeholder="Enter a secure passphrase"
                      required
                    />
                    <Form.Text className="text-muted">
                      Your passphrase should be at least 8 characters and include a mix of letters, numbers, and special characters.
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Passphrase Strength</Form.Label>
                    <ProgressBar 
                      now={strength} 
                      variant={getStrengthColor()} 
                      className="mb-2" 
                    />
                    <Form.Text className={`text-${getStrengthColor()}`}>
                      {strength < 30 && 'Very Weak'}
                      {strength >= 30 && strength < 50 && 'Weak'}
                      {strength >= 50 && strength < 80 && 'Good'}
                      {strength >= 80 && 'Strong'}
                    </Form.Text>
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Passphrase</Form.Label>
                    <Form.Control
                      type="password"
                      value={confirmPassphrase}
                      onChange={(e) => setConfirmPassphrase(e.target.value)}
                      placeholder="Confirm your passphrase"
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-4">
                    <Form.Check
                      type="checkbox"
                      id="rememberPassphrase"
                      label="Remember my passphrase on this device"
                      checked={rememberPassphrase}
                      onChange={(e) => setRememberPassphrase(e.target.checked)}
                    />
                    <Form.Text className="text-muted">
                      This will keep you logged in on this device. Only select this option on personal, secure devices.
                    </Form.Text>
                  </Form.Group>
                  
                  <div className="d-grid">
                    <Button 
                      variant="primary" 
                      type="submit" 
                      disabled={loading || !passphrase || !confirmPassphrase}
                    >
                      {loading ? 'Setting up...' : 'Complete Setup'}
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SetupPassphrase;